import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Base: tactile press feedback applies to ALL variants. `motion-safe:` defers to
  // user's prefers-reduced-motion setting. Pure CSS — no Framer Motion needed.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-display font-semibold ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 motion-safe:active:scale-[0.97] motion-safe:active:translate-y-px active:duration-75",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/85 hover:shadow-lg hover:shadow-primary/30 motion-safe:hover:-translate-y-px active:shadow-none",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/85 hover:shadow-lg hover:shadow-destructive/30 motion-safe:hover:-translate-y-px",
        outline: "btn-gradient-outline motion-safe:hover:-translate-y-px",
        secondary: "btn-gradient-outline motion-safe:hover:-translate-y-px",
        ghost: "text-muted-foreground hover:text-primary hover:bg-transparent",
        link: "text-secondary underline-offset-4 hover:text-primary hover:underline",
      },
      size: {
        default: "h-10 px-6 py-2 text-sm rounded-full",
        sm: "h-9 px-4 text-xs rounded-full",
        lg: "h-12 px-8 text-base rounded-full",
        icon: "h-10 w-10 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
